import type { MemberProfile } from "../api/members";
import { AvatarBadge } from "./AvatarBadge";
import { DataList } from "./DataList";

interface MembershipCardProps {
  profile: MemberProfile;
}

function categoryLabel(category: MemberProfile["membership_category"]): string {
  return category === "efetivo" ? "Efetivo" : "Colaborador";
}

function statusLabel(status: MemberProfile["status"]): string {
  if (status === "ativo") return "Ativo";
  if (status === "suspenso_disciplinar") return "Suspenso";
  return "Desligado";
}

function formatDate(value: string | null): string {
  if (!value) return "Não informada";

  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "Não informada";

  return new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(date);
}

function activeDepartments(profile: MemberProfile): string[] {
  return Array.from(
    new Set(
      profile.department_assignments
        .filter((assignment) => !assignment.ended_at)
        .map((assignment) => assignment.department.trim())
        .filter(Boolean),
    ),
  );
}

export function MembershipCard({ profile }: MembershipCardProps) {
  const displayName = profile.preferred_name?.trim() || profile.full_name;
  const departments = activeDepartments(profile);
  const status = statusLabel(profile.status);

  return (
    <section
      className="membership-card"
      aria-labelledby="membership-card-title"
      aria-describedby="membership-card-status"
    >
      <div className="membership-card__header">
        <div className="membership-card__brand">
          <span className="membership-card__brand-crest" aria-hidden="true">IEDC</span>
          <div>
            <p className="membership-card__eyebrow">Identificação institucional</p>
            <h2 id="membership-card-title">Carteirinha institucional</h2>
          </div>
        </div>
        <p
          id="membership-card-status"
          className={`membership-card__status-badge membership-card__status-badge--${profile.status}`}
        >
          <span className="membership-card__status-dot" aria-hidden="true" />
          Situação: <strong>{status}</strong>
        </p>
      </div>

      <div className="membership-card__identity">
        <AvatarBadge name={displayName} username={profile.username ?? displayName} size="lg" />
        <div className="membership-card__identity-details">
          <p className="membership-card__name">{displayName}</p>
          <p className="membership-card__category-tag">
            {categoryLabel(profile.membership_category)} · IEDC
          </p>
        </div>
      </div>

      <div className="membership-card__metadata">
        <DataList
          variant="inverse"
          columns={3}
          items={[
            { label: "Categoria", value: categoryLabel(profile.membership_category) },
            {
              label: "Departamento",
              value: departments.length > 0 ? departments.join(", ") : "Sem departamento atribuído no momento",
            },
            { label: "Efetivação", value: formatDate(profile.effective_since) },
          ]}
        />
      </div>

      <div className="membership-card__verification" aria-label="Validação digital">
        <span className="membership-card__verification-mark" aria-hidden="true">ID</span>
        <div className="membership-card__verification-text">
          <p className="membership-card__verification-title">Validação digital</p>
          <p className="membership-card__verification-copy">
            O código seguro da carteirinha será disponibilizado quando o serviço institucional de validação estiver ativo.
          </p>
        </div>
      </div>
    </section>
  );
}

export function MembershipCardSkeleton() {
  return (
    <section className="membership-card membership-card--loading" aria-label="Carregando carteirinha institucional" aria-live="polite">
      <span className="membership-card__skeleton membership-card__skeleton--avatar" />
      <span className="membership-card__skeleton membership-card__skeleton--title" />
      <span className="membership-card__skeleton membership-card__skeleton--line" />
      <span className="visually-hidden">Carregando carteirinha institucional…</span>
    </section>
  );
}
